import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "aws-amplify";
import { INewRxStepHandoff } from "../types/PrescriptionIngestionTypes";
import { matchPrescriptionId, PrescriptionMatchTarget } from "../utils/PrescriptionMatcher";
import { getApiErrorMessage } from "../../../utils/apiError";

export const AUTO_RETRY_INTERVAL_MS = 15000;
export const MAX_AUTO_RETRIES = 8;

const findPrescriptionByDrug = async (personId: string, target: PrescriptionMatchTarget): Promise<string> => {
  const response = await API.get(
    "ApaqPortalAPI",
    `/order/prescription/get-prescriptions?personId=${encodeURIComponent(personId)}`,
    {}
  );
  return matchPrescriptionId(response?.prescriptions || [], target);
};

export interface IPrescriptionLookup {
  prescriptionId: string;
  setPrescriptionId: (prescriptionId: string) => void;
  isLookingUp: boolean;
  lookupFailed: boolean;
  lookupErrorMessage: string;
  isAutoRetrying: boolean;
  refresh: () => Promise<void>;
}

/**
 * Strictly matches the prescription for the uploaded drug, skipping the handoff's excluded
 * prescription. Runs on mount, then retries quietly while the prescription is not queryable yet.
 */
export const usePrescriptionLookup = (handoff?: INewRxStepHandoff): IPrescriptionLookup => {
  const [prescriptionId, setPrescriptionId] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [lookupErrorMessage, setLookupErrorMessage] = useState("");
  const [backgroundErrorMessage, setBackgroundErrorMessage] = useState("");
  const [autoRetries, setAutoRetries] = useState(0);

  const latestRequestRef = useRef(0);
  useEffect(() => {
    return () => {
      latestRequestRef.current++;
    };
  }, []);

  const personId = handoff?.personId;
  const drugDescription = handoff?.drugDescription;
  const ndc = handoff?.ndc;
  const excludePrescriptionId = handoff?.excludePrescriptionId;
  const canLookup = !!personId && !!(drugDescription || ndc);

  const runLookup = useCallback(
    async (background: boolean) => {
      if (!canLookup) {
        return;
      }
      const requestId = ++latestRequestRef.current;
      const isCurrent = () => requestId === latestRequestRef.current;
      if (!background) {
        setIsLookingUp(true);
        setLookupFailed(false);
        setLookupErrorMessage("");
      }
      try {
        const matched = await findPrescriptionByDrug(personId || "", { drugDescription, ndc, excludePrescriptionId });
        if (!isCurrent()) {
          return;
        }
        setPrescriptionId(matched || "");
        setLookupFailed(!matched);
        setBackgroundErrorMessage("");
      } catch (err) {
        if (!isCurrent()) {
          return;
        }
        console.warn("Failed to look up prescriptionId:", err);
        setLookupFailed(true);
        const message = getApiErrorMessage(err, "Failed to retrieve prescriptions.");
        if (background) {
          setBackgroundErrorMessage(message);
        } else {
          setLookupErrorMessage(message);
        }
      } finally {
        if (isCurrent() && !background) {
          setIsLookingUp(false);
        }
      }
    },
    [canLookup, personId, drugDescription, ndc, excludePrescriptionId]
  );

  const refresh = useCallback(async () => {
    setAutoRetries(0);
    setBackgroundErrorMessage("");
    await runLookup(false);
  }, [runLookup]);

  useEffect(() => {
    if (canLookup) {
      refresh();
    }
  }, [canLookup, refresh]);

  const retriesExhausted = autoRetries >= MAX_AUTO_RETRIES;
  const isAutoRetrying = canLookup && lookupFailed && !lookupErrorMessage && !retriesExhausted;
  useEffect(() => {
    if (!isAutoRetrying) {
      return;
    }
    const timer = setTimeout(() => {
      setAutoRetries((count) => count + 1);
      runLookup(true);
    }, AUTO_RETRY_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isAutoRetrying, autoRetries, runLookup]);

  return {
    prescriptionId,
    setPrescriptionId,
    isLookingUp,
    lookupFailed,
    lookupErrorMessage: lookupErrorMessage || (retriesExhausted ? backgroundErrorMessage : ""),
    isAutoRetrying,
    refresh,
  };
};
